/**
 * The `ApiResponse` envelope around a WebAuthn options payload (plan §2.2.2 · AD-10).
 *
 * `postAuthJson` resolves `undefined` for a body-less success, which is right
 * for the verify routes (204) and wrong for an options leg: a ceremony cannot
 * start without its options, so absence of a `data` record throws.
 *
 * Shared by both credential surfaces because the unwrap carries no taxonomy —
 * each caller passes its OWN error factory, so `passkey-login.tsx` keeps
 * `CeremonyOptionsError` and `add-passkey.tsx` keeps `AddPasskeyOptionsError`
 * and neither error class becomes a cross-component contract. That is the
 * distinction the add-passkey header draws: the error classes stay private,
 * the data-record rule gets one implementation.
 */
import { isRecord } from './is-record';

/** Unwrap `{ success, data }` to the options record, or throw the caller's error. */
export function unwrapOptionsEnvelope(
  payload: unknown,
  makeError: () => Error,
): Record<string, unknown> {
  if (!isRecord(payload)) throw makeError();
  const data = payload['data'];
  if (!isRecord(data)) throw makeError();
  return data;
}

/**
 * Reject a present-but-unusable registration options record.
 *
 * `isRecord` alone is not enough to hand the payload to `startRegistration`:
 * it immediately calls `base64URLStringToBuffer` on `challenge` and on
 * `user.id`, so a malformed envelope throws a raw `TypeError` from library
 * internals. That lands in the generic 'unknown' arm AND reaches
 * `reportProgrammingError`, which rethrows to the host — a SERVER fault
 * reported as our own defect and surfaced as an uncaught window error.
 *
 * Checked here rather than at every call site so the boundary parse and the
 * cast that follows it stay adjacent. Only the two fields the library
 * dereferences up front are asserted: a narrower payload failure is the
 * authenticator's to report, not ours to predict.
 */
export function assertRegistrationOptions(
  options: Record<string, unknown>,
  makeError: () => Error,
): void {
  if (typeof options['challenge'] !== 'string') throw makeError();
  const user = options['user'];
  if (!isRecord(user) || typeof user['id'] !== 'string') throw makeError();
}
